import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import CoverageStrip from '../../components/feature/CoverageStrip';
import SiteWidgets from '../../components/feature/SiteWidgets';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

function SolarQuoteForm() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!postcode.trim()) { setError('Please enter your postcode'); return; }
    setError('');
    setSubmitting(true);

    // Submit to form endpoint in background
    try {
      const body = new URLSearchParams({ service: 'Solar Installation', postcode });
      fetch('https://readdy.ai/api/form/d7426jr41deorl02el20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // Silent fail - still redirect
    }

    // Redirect to solar quote wizard
    const params = new URLSearchParams({ service: 'Solar Installation', postcode });
    navigate(`/solar-quote?${params.toString()}`);
  };

  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3">
          <i className="ri-checkbox-circle-fill text-[#7AC943] text-6xl" />
        </div>
        <h3 className="font-bold text-xl text-[#1F2933] mb-2">Taking you to the quote wizard...</h3>
        <p className="text-gray-500 text-sm">Just a moment while we set up your solar quote.</p>
      </div>
    );
  }

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <input
        type="text"
        name="postcode"
        value={postcode}
        onChange={(e) => setPostcode(e.target.value.toUpperCase())}
        placeholder="Enter your postcode"
        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm text-[#1F2933] focus:outline-none focus:border-[#4CAF50] transition-colors"
        maxLength={8}
      />
      {error && <p className="text-red-500 text-xs">{error}</p>}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-gradient-to-r from-[#145A32] to-[#4CAF50] text-white font-bold py-4 rounded-xl text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70"
      >
        {submitting ? 'Getting your quote...' : 'Get my free solar quote →'}
      </button>
      <p className="text-xs text-gray-400 text-center">No obligation. Fixed prices. Response within 24 hours.</p>
    </form>
  );
}

const benefits = [
  { icon: 'ri-money-pound-circle-line', title: 'Cut bills by up to 70%', desc: 'Most homeowners see dramatic reductions in their electricity costs from the first month.' },
  { icon: 'ri-sun-line', title: 'Generate free energy', desc: 'Generate your own clean electricity and reduce reliance on the grid all year round.' },
  { icon: 'ri-home-heart-line', title: 'Increase property value', desc: 'Homes with solar installations sell for up to 14% more according to recent studies.' },
  { icon: 'ri-leaf-line', title: 'Cut your carbon footprint', desc: 'The average 4kW solar system saves around 1.3 tonnes of CO₂ per year.' },
  { icon: 'ri-award-line', title: 'MCS accredited install', desc: 'All installations carried out by certified MCS-accredited engineers for quality assurance.' },
  { icon: 'ri-shield-check-line', title: '25-year panel warranty', desc: 'Premium panels backed by a 25-year performance guarantee for total peace of mind.' },
];

const packages = [
  { size: '3kW', panels: '6 panels', output: '~2,700 kWh/yr', price: '£4,995', desc: 'Ideal for 1–2 bedroom homes or apartments', popular: false },
  { size: '4kW', panels: '10 panels', output: '~3,400 kWh/yr', price: '£5,995', desc: 'Perfect for a typical 3-bedroom family home', popular: true },
  { size: '6kW', panels: '15 panels', output: '~5,200 kWh/yr', price: '£7,495', desc: 'Best for larger homes or high energy users', popular: false },
];

const faqs = [
  { q: 'How long does a solar installation take?', a: 'Most installations are completed in a single day. Our engineers will be in and out quickly with minimal disruption to your household.' },
  { q: 'Do I need planning permission for solar panels?', a: 'In most cases, no. Solar panels are classed as permitted development in the UK, so planning permission is rarely required.' },
  { q: 'What happens on cloudy days?', a: 'Solar panels still generate electricity on cloudy days — just at a reduced rate. Modern panels are very efficient even in diffuse light.' },
  { q: 'Can I add a battery later?', a: 'Absolutely. Our systems are designed to be battery-ready, so you can add storage at any point in the future.' },
];

export default function SolarPage() {
  const [visible, setVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const benefitsAnim = useScrollAnimation();
  const packagesAnim = useScrollAnimation();
  const faqAnim = useScrollAnimation();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-white font-sans">
      <Header />

      {/* Hero */}
      <section className="bg-[#145A32] pt-24 md:pt-40 pb-0 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className={`flex-1 pb-8 md:pb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <Link to="/" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-6 transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm" /></div>
                Back to home
              </Link>
              <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-3">SOLAR PANELS</div>
              <h1 className="font-heading font-black text-white text-4xl md:text-5xl leading-tight mb-4">
                Solar panels for<br />your home
              </h1>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6 max-w-lg">
                Reduce your electricity bills by up to 70%, generate clean energy and protect yourself from rising energy prices. Fixed prices, MCS certified engineers.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#get-quote" className="bg-[#7AC943] hover:bg-[#6ab538] text-white font-bold px-6 py-3.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                  Get a free quote
                </a>
                <a href="#packages" className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                  View packages
                </a>
              </div>
              <div className="flex flex-wrap gap-6 mt-8">
                {['MCS Accredited', '25-yr Warranty', 'Finance Available'].map(t => (
                  <div key={t} className="flex items-center gap-2 text-white/80 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-[#7AC943]" /></div>
                    {t}
                  </div>
                ))}
              </div>
            </div>
            <div className={`flex-1 flex items-end justify-center md:justify-end transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <img
                src="https://public.readdy.ai/ai/img_res/091044c0904f1d4f64231f736e27c535.jpg"
                alt="Solar panels on home"
                className="w-full max-w-lg h-[300px] md:h-[420px] object-cover object-top rounded-t-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div
            ref={benefitsAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${benefitsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">WHY SOLAR?</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">The benefits of going solar</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                style={{ transitionDelay: `${i * 80}ms` }}
                className={`bg-[#f7f8fa] rounded-xl p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-300 ${benefitsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#145A32]/10 mb-4">
                  <i className={`${b.icon} text-[#145A32] text-xl`} />
                </div>
                <h3 className="font-bold text-[#1F2933] text-base mb-2">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="py-14 md:py-20 bg-[#f7f8fa]">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div
            ref={packagesAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${packagesAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">PACKAGES</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">Choose your solar package</h2>
            <p className="text-gray-500 text-sm mt-2">All prices are fully installed, including VAT and scaffolding.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {packages.map((pkg, i) => (
              <div
                key={pkg.size}
                style={{ transitionDelay: `${i * 100}ms` }}
                className={`bg-white rounded-2xl border-2 p-6 relative transition-all duration-300 hover:-translate-y-1 ${pkg.popular ? 'border-[#4CAF50]' : 'border-gray-100'} ${packagesAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4CAF50] text-white text-[10px] font-bold px-4 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                    Most Popular
                  </div>
                )}
                <div className="text-[#145A32] font-black text-3xl mb-1">{pkg.size}</div>
                <div className="text-gray-500 text-xs mb-3">{pkg.panels} · {pkg.output}</div>
                <div className="text-[#1F2933] font-bold text-2xl mb-2">{pkg.price}</div>
                <p className="text-gray-500 text-sm mb-5 leading-relaxed">{pkg.desc}</p>
                <a href="#get-quote" className="block w-full text-center bg-gradient-to-r from-[#145A32] to-[#4CAF50] text-white font-bold py-3 rounded-xl text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap">
                  Get this package
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Strip */}
      <CoverageStrip />

      {/* FAQs */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div
            ref={faqAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${faqAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">FAQ</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">Frequently asked questions</h2>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                style={{ transitionDelay: `${i * 80}ms` }}
                className={`border border-gray-100 rounded-xl overflow-hidden transition-all duration-500 ${faqAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <button
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-[#1F2933] text-sm pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#4CAF50] text-lg transition-transform duration-200`} />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40' : 'max-h-0'}`}>
                  <p className="px-5 pb-5 text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Quote form */}
      <section id="get-quote" className="py-14 md:py-20 bg-[#145A32]">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
          <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-3">GET STARTED</div>
          <h2 className="font-heading font-black text-white text-2xl md:text-3xl mb-3">
            Get your free solar quote today
          </h2>
          <p className="text-white/70 text-sm mb-8 leading-relaxed">
            Enter your postcode and we&apos;ll arrange a no-obligation survey from an MCS-certified engineer in your area.
          </p>
          <div className="bg-white rounded-2xl p-6 md:p-8">
            <SolarQuoteForm />
          </div>
        </div>
      </section>

      <Footer />
      <SiteWidgets />
    </main>
  );
}
